import React, { useState, useRef, useCallback } from 'react';
import { useExportsActions } from '../hooks/useExportsActions';
import { Flex, SubPage, Textinput, Loading, Typo, Button } from '../../../ui';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { CONTENT_SMALL } from '../../../utils/media';
import { useIsAdmin, useLocalization } from '../../auth/hooks';
import { CSVLink } from '../../createCsv';
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';
import PageControls from '../../../ui/Page/PageControls';

const SalesExport = () => {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [exportData, setExportData] = useState(null);
  const [customStoreId, setCustomStoreId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const locale = useLocalization();
  const storeIdRef = useRef(null);
  const isAmin = useIsAdmin();

  const { salesExport } = useExportsActions();

  const generateReport = useCallback(async () => {
    if (startDate && endDate) {
      setIsLoading(true);
      try {
        const result = await salesExport({
          customStoreId,
          startDate: startDate?.format('YYYY-MM-DD'),
          endDate: endDate?.format('YYYY-MM-DD'),
        });
        setExportData(result);
      } catch (error) {
        // Error handling is now in the actions hook
      } finally {
        setIsLoading(false);
      }
    }
  }, [startDate, endDate, customStoreId, salesExport]);

  const data =
    !!exportData?.charges &&
    Object.values(exportData?.charges)?.map((item) => {
      const disc = Object.values(item.children)
        .map((cut) => (cut.type === 'discount' ? cut.description : null))
        .filter(Boolean);

      const coursioCut = Object.values(item.children)
        .map((cut) => (cut.type === 'coursio_cut' ? cut.ex_tax : null))
        .filter(Boolean);

      const refund = Object.values(item.children)
        .map((cut) => (cut.type === 'refund' ? cut.charge : null))
        .filter(Boolean);

      return {
        // '#': item.id,
        Type: item.type,
        Date: item.created,
        email: item.email,
        Name: item.name,
        'Course id': item.object_id,
        'Course name ': item.description,
        Country: item.country,
        Provider: item.provider,
        Discount: disc,
        'Price w/o VAT': item.ex_tax,
        'VAT %': item.tax_value,
        Charged: item.charge,
        refund: refund,
        Currency: item.currency,
        'Coursio cut': coursioCut,
      };
    });

  const handleStoreIdChange = useCallback(
    debounce(() => {
      const {
        current: { value },
      } = storeIdRef;

      if (value.length < 1) setCustomStoreId(null);
      else setCustomStoreId(value);
    }, 1000),
    [storeIdRef, setCustomStoreId],
  );

  return (
    <SubPage size={CONTENT_SMALL}>
      <Flex width={'100%'} gap={40} direction={'column'}>
        {isAmin && (
          <Textinput
            autoFocus
            margin='dense'
            id='storeId'
            label={'store id (option only available for admins)'}
            type='text'
            fullWidth
            inputRef={storeIdRef}
            onChange={handleStoreIdChange}
          />
        )}
        {locale && (
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
            <Flex gap='20'>
              <DatePicker label={t('globals.startDate')} value={startDate} onChange={(newValue) => setStartDate(newValue)} />
              <DatePicker label={t('globals.endDate')} value={endDate} onChange={(newValue) => setEndDate(newValue)} />
              <Button icon='Sync' disabled={!(startDate && endDate)} onClick={generateReport}>
                {t('export.generateReport')}
              </Button>
            </Flex>
          </LocalizationProvider>
        )}
      </Flex>
      <PageControls
        openModelButton={
          isLoading ? (
            <Loading />
          ) : data?.length ? (
            <CSVLink
              data={data}
              filename={`sales_${storeIdRef.current ? storeIdRef.current?.value : 'myStore'}_${dayjs(startDate).format('YYYY-MM-DD')}_${dayjs(endDate).format(
                'YYYY-MM-DD',
              )}.csv`}
            >
              {t('export.downloadReport')}
            </CSVLink>
          ) : (
            <Typo.Text>{!!data ? t('export.noDataForDates') : t('export.selectDatesForReport')}</Typo.Text>
          )
        }
      />
    </SubPage>
  );
};

export default SalesExport;
